import { routes } from '@dap/common/consts';
import { useUser, useBrandDetails, useSelectedBrandKey } from '@dap/common/data-access';
import { NavRoute, NavWithSubRoutes } from '@shared/components';
import { SanityIcon } from '@shared/custom-icons';
import { useLocation, matchPath } from 'react-router';
import { hasSanityDapAccess } from '@dap/sanity/utils';
import { SanityHuskjedeUserRole } from '@dap/common/types';
import { config } from '@dap/app/config';
import { UserData } from '@dap/memberadmin/types';
import { LocationOverview } from '@dap/memberadmin/types';

/**
 * Returns true if the user has access to the sanity studio for a given brand
 *
 * A user has acess if they are a member of the brand and have any of the following roles related to that brand:
 * SanityHuskjedeUserRole.Editor
 * SanityHuskjedeUserRole.Administrator
 *
 * @param user - The user to check access for
 * @param brandKey - The key of the brand to check access for
 * @param locationBrands - A mapping of location IDs and their brand
 * @returns True if the user has access to the sanity studio for the given brand, false otherwise
 */
function hasSanityBrandAccess(
  user: UserData,
  brandKey: string,
  locations: LocationOverview[]
): boolean {
  const hasAccessToBrand = !!user.brands.find((b) => b.key === brandKey);
  const hasSanityRole = user.roles.some((role) => {
    // If role is of type brand, check if the user has the admin role to that brand
    const hasAdminRole =
      role.role.key === SanityHuskjedeUserRole.Administrator &&
      role.type === 'brand' &&
      role.orgUnitKey === brandKey;

    // If role is of type location, check if the user has the editor role, and if the location for the role is a location for the brand
    const hasEditorRole =
      role.role.key === SanityHuskjedeUserRole.Editor &&
      role.type === 'location' &&
      locations.some((location) => location.key === role.orgUnitKey);

    return hasAdminRole || hasEditorRole;
  });

  return hasAccessToBrand && hasSanityRole;
}

export const useSanityRoute = (): NavRoute | NavWithSubRoutes | undefined => {
  const userData = useUser();
  const { pathname } = useLocation();
  const brandKey = useSelectedBrandKey();
  const { locations } = useBrandDetails();

  // Build a list of all Sanity routes the user should have
  const studioRoutes: NavRoute[] = [];

  // Check if the user has access to the DAP studio
  if (hasSanityDapAccess(userData)) {
    studioRoutes.push({
      title: 'DAP',
      Icon: SanityIcon,
      isActive: !!matchPath(`${routes.sanity}/${routes.sanityStudioDAP}`, pathname),
      to: `${routes.sanity}/${routes.sanityStudioDAP}`,
    });
  }

  // TODO ASO Rewrite to use locations from brand to check if the user has access to the sanity studio for that brand
  // Iterate all the externally hosted sanity studios, and add a route if the current brandKey matches the external studio and the user has access to that brand
  Object.entries(config.externalSanityStudios ?? {}).forEach(([brand, studio]) => {
    if (brand === brandKey && hasSanityBrandAccess(userData, brandKey, locations)) {
      studioRoutes.push({
        title: `${studio.title}`,
        to: '',
        Icon: SanityIcon,
        isActive: false,
        href: studio.url,
        external: true,
      });
    }
  });

  // If the user has access to more than one sanity studio, return a NavWithSubRoutes object with the routes as subroutes
  if (studioRoutes.length > 1) {
    return {
      title: 'Sanity',
      Icon: SanityIcon,
      isActive: studioRoutes.some((route) => route.isActive) ?? false,
      initialOpen: false,
      subRoutes: studioRoutes.map((route) => ({
        title: route.title,
        Icon: SanityIcon,
        isActive: route.isActive,
        to: route.to,
        external: route.external,
        href: route.href,
      })),
    };
  }

  return studioRoutes.length === 1 ? { ...studioRoutes[0], title: 'Sanity' } : undefined;
};
